import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUnifiedAuth } from "../auth/useUnifiedAuth";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useNavigationHistoryStore } from "./useNavigationHistoryStore";
import { natsClient } from "../services/NATSClient";
import { decodeToken } from "../utils/tokenUtils";
import {
  discoverCandidateRoleClaims,
  resolveUserRoleFromClaims,
} from "../utils/userRole";
import { isWhatsNewDebugEnabled } from "../utils/debugFlags";
import { isDemoMode } from "../services/demoMode";
import { useWhatsNewStore } from "../store/WhatsNewStore";
import type {
  WhatsNewMachine,
  WhatsNewVisit,
} from "../services/WhatsNewService";

export type { BriefingStatus } from "../store/WhatsNewStore";

/**
 * How long to wait for NATS before reading the KV marker.
 *
 * Reading too early falls back to localStorage, which on a new browser looks like
 * "never briefed" and would regenerate unnecessarily — defeating the
 * cross-browser requirement.
 */
const NATS_WAIT_MS = 8000;

/** How long to wait for the machine park to arrive before giving up. */
const PARK_WAIT_MS = 15000;

function waitForNats(timeoutMs: number): Promise<boolean> {
  if (natsClient.isConnected()) return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      natsClient.offStatusChange(onStatus);
      clearTimeout(timer);
      resolve(value);
    };
    const onStatus = (status: { isConnected: boolean }) => {
      if (status.isConnected) finish(true);
    };
    const timer = setTimeout(() => finish(natsClient.isConnected()), timeoutMs);
    natsClient.onStatusChange(onStatus);
  });
}

/**
 * Gathers the context for the briefing and hands it to the shared store.
 *
 * Mounted twice on purpose: once headlessly at app root (so the briefing is ready
 * before the user opens the tab) and once in the Welcome tab (which renders it).
 * `useWhatsNewStore.ensure` is single-flight and idempotent per user, so the
 * second caller is a no-op rather than a second LLM call.
 *
 * Triggering is deliberately not wired to a Keycloak auth event:
 * `ConditionalAuthProvider` uses `onLoad: "login-required"` with the standard
 * flow, so `onAuthSuccess` fires on every page load and cannot tell a fresh login
 * from a refresh. The once-per-day marker in NATS KV is what enforces "not on
 * every refresh", and it works across browsers, which an auth event never could.
 */
export function useWhatsNewBriefing() {
  const { authenticated, token, user } = useUnifiedAuth();
  const { i18n } = useTranslation();
  const { history, loading: historyLoading } = useNavigationHistoryStore();
  const machines = useSAPDataStore((s) => s.machines);
  const machinesWithData = useSAPDataStore((s) => s.machinesWithData);

  const status = useWhatsNewStore((s) => s.status);
  const summary = useWhatsNewStore((s) => s.summary);
  const generatedAt = useWhatsNewStore((s) => s.generatedAt);
  const windowStart = useWhatsNewStore((s) => s.windowStart);
  const error = useWhatsNewStore((s) => s.error);
  const ensure = useWhatsNewStore((s) => s.ensure);
  const markEmpty = useWhatsNewStore((s) => s.markEmpty);

  /** Stable per-user identity for the KV key; `sub` survives a username change. */
  const userKey = useMemo(() => {
    const claims = token ? decodeToken(token) : null;
    return claims?.sub || user?.username || null;
  }, [token, user?.username]);

  const role = useMemo(() => {
    const claims = token
      ? (decodeToken(token) as Record<string, unknown> | null)
      : null;
    const resolved = resolveUserRoleFromClaims(claims);

    if (isWhatsNewDebugEnabled()) {
      if (resolved) {
        console.info(
          `🧑‍🏭 What's New: role "${resolved.role}" from claim "${resolved.claim}"`,
        );
      } else {
        // The role is a free-text Keycloak *user attribute*, and the claim name it
        // maps to is configured there, not here. When nothing matches, this lists
        // the token's own non-standard string claims so the right name can be
        // added (or set via VITE_USER_ROLE_CLAIM).
        console.info(
          "🧑‍🏭 What's New: no role claim matched. Candidate claims in this token:",
          discoverCandidateRoleClaims(claims),
        );
      }
    }

    return resolved?.role ?? null;
  }, [token]);

  /**
   * Expose the role resolution for inspection at `window.__whatsNew`.
   *
   * keycloak-js holds the access token in memory, not in storage, so there is no
   * way to decode it from the console unaided. This puts the decoded claims, the
   * resolved role and the claim it came from somewhere reachable, which is the
   * only reliable way to tell "the attribute isn't in the token" apart from "our
   * resolver missed it".
   *
   * An effect rather than part of the memo above, so render stays side-effect free.
   */
  useEffect(() => {
    if (!isWhatsNewDebugEnabled()) return;

    const claims = token
      ? (decodeToken(token) as Record<string, unknown> | null)
      : null;
    const resolved = resolveUserRoleFromClaims(claims);

    (window as unknown as Record<string, unknown>).__whatsNew = {
      role: resolved?.role ?? null,
      fromClaim: resolved?.claim ?? null,
      /** Every non-standard string claim, i.e. where a role could be hiding. */
      candidateClaims: discoverCandidateRoleClaims(claims),
      /** All claim names present, to confirm whether the mapper fired at all. */
      allClaimNames: claims ? Object.keys(claims).sort() : [],
      claims,
    };
  }, [token]);

  /**
   * Machines with data first — a briefing about machines that report nothing is
   * noise. Falls back to the full park when the filtered list isn't ready.
   */
  const park: WhatsNewMachine[] = useMemo(() => {
    const source =
      machinesWithData && machinesWithData.length > 0
        ? machinesWithData
        : machines;
    return (source ?? [])
      .map((m) => ({
        id: m.maschinennummer || m.maschineBK || m.machineId || "",
        name: m.machineName,
        model: m.bautyp || m.machineType,
        line: m.lineId,
        factory: m.factoryName,
      }))
      .filter((m) => m.id);
  }, [machines, machinesWithData]);

  const visited: WhatsNewVisit[] = useMemo(
    () =>
      (history ?? []).map((event) => ({
        type: event.type,
        id: event.machineData?.maschinennummer || event.id,
        name: event.name || event.machineData?.machineName,
        lastViewedAt: event.timestamp,
      })),
    [history],
  );

  /**
   * Latest inputs, read through a ref so the trigger effect doesn't re-run every
   * time the park or history array is rebuilt.
   */
  const inputRef = useRef({ userKey, role, park, visited, language: i18n.language });
  inputRef.current = { userKey, role, park, visited, language: i18n.language };

  /** Guards against re-triggering from re-renders or a Strict Mode double mount. */
  const startedForRef = useRef<string | null>(null);

  useEffect(() => {
    // Demo mode has no per-user briefing: the park isn't the user's, and the
    // insight state is keyed to the real account. `WhatsNewStore.ensure` also
    // refuses, but stopping here avoids the NATS wait entirely.
    if (isDemoMode()) return;
    if (!authenticated || !userKey) return;
    if (historyLoading) return;
    if (startedForRef.current === userKey) return;

    // The park arrives with the SAP tree fetch. Briefing an empty park is
    // pointless, so hold briefly — but don't hang forever if the user has none.
    if (park.length === 0) {
      const timer = setTimeout(() => {
        if (startedForRef.current !== userKey && park.length === 0) {
          startedForRef.current = userKey;
          markEmpty(userKey);
        }
      }, PARK_WAIT_MS);
      return () => clearTimeout(timer);
    }

    startedForRef.current = userKey;

    void (async () => {
      await waitForNats(NATS_WAIT_MS);
      const current = inputRef.current;
      if (!current.userKey) return;
      await ensure({ ...current, userKey: current.userKey }, false);
    })();
  }, [authenticated, userKey, historyLoading, park.length, ensure, markEmpty]);

  const regenerate = useCallback(() => {
    if (isDemoMode()) return;
    const current = inputRef.current;
    if (!current.userKey) return;
    void ensure({ ...current, userKey: current.userKey }, true);
  }, [ensure]);

  return {
    status,
    summary,
    generatedAt,
    windowStart,
    error,
    role,
    regenerate,
    machineCount: park.length,
  };
}
